import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { supabase } from './config/supabase';
import AuthGuard from './components/auth/AuthGuard';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import UpdatePassword from './pages/UpdatePassword';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Contacts from './pages/Contacts';
import Opportunities from './pages/Opportunities';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import Settings from './pages/Settings';
import LoadingSpinner from './components/common/LoadingSpinner';

import Teams from './pages/Teams';

function App() {
  const { loading, user } = useAuth();

  useEffect(() => {
    console.log('🔵 [App] Component state:', {
      loading,
      hasUser: !!user,
      timestamp: new Date().toISOString()
    });
  }, [loading, user]);

  if (loading) {
    console.log('⏳ [App] Showing initial loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Loading application...</p>
          <p className="mt-2 text-xs text-gray-400">
            Check browser console for detailed logs
          </p>
        </div>
      </div>
    );
  }

  console.log('✅ [App] Rendering router');
  return (
    <Router>
      <AuthEventHandler />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route
          path="/*"
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="opportunities" element={<Opportunities />} />
          <Route path="teams" element={<Teams />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </Router>
  );
}

// Helper to handle Auth Events inside Router context
const AuthEventHandler = () => {
  const { navigate } = useAuthRedirect();
  return null;
}

const useAuthRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/update-password');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return { navigate };
};

export default App;